// src/App.js
import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import Sidebar from './components/Sidebar/Sidebar';
import Topbar from './components/Topbar/Topbar';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import { NotificationProvider } from './context/NotificationContext';
import NotificationToast from './components/NotificationToast/NotificationToast';

// React.lazy 코드 스플리팅
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Products = lazy(() => import('./pages/Products/Products'));
const EditProduct = lazy(() => import('./pages/Products/EditProduct'));
const AddProduct = lazy(() => import('./pages/Products/AddProduct'));
const QuickAddProduct = lazy(() => import('./pages/Products/QuickAddProduct'));
const Customers = lazy(() => import('./pages/Customers/Customers'));
const CustomerDetail = lazy(() => import('./pages/Customers/CustomerDetail'));
const Orders = lazy(() => import('./pages/Oders/Orders'));
const OrderDetail = lazy(() => import('./pages/Oders/OrderDetail'));
const ProductRequests = lazy(() => import('./pages/ProductRequests/ProductRequests'));
const Pickups = lazy(() => import('./pages/Pickups/Pickups'));
const Recommends = lazy(() => import('./pages/Recommends/Recommends'));
const Similarity = lazy(() => import('./pages/Similarity/Similarity'));
const Themes = lazy(() => import('./pages/Themes/Themes'));
const Banners = lazy(() => import('./pages/Banners/Banners'));
const CategoryIcons = lazy(() => import('./pages/CategoryIcons/CategoryIcons'));
const HomeSections = lazy(() => import('./pages/HomeSections/HomeSections'));
const ProductAnalytics = lazy(() => import('./pages/ProductAnalytics/ProductAnalytics'));
const Inventory = lazy(() => import('./pages/Inventory/Inventory'));
const ShoppingList = lazy(() => import('./pages/ShoppingList/ShoppingList'));
const HsCodes = lazy(() => import('./pages/HsCodes/HsCodes'));
const Tools = lazy(() => import('./pages/Tools/Tools'));
const Profit = lazy(() => import('./pages/Profit/Profit'));
const Settlements = lazy(() => import('./pages/Settlements/Settlements'));
const PricingSimulator = lazy(() => import('./pages/PricingSimulator/PricingSimulator'));
const PriceCompare = lazy(() => import('./pages/PriceCompare/PriceCompare'));
const PriceSetting = lazy(() => import('./pages/PriceSetting/PriceSetting'));
const OpsGuide = lazy(() => import('./pages/OpsGuide/OpsGuide'));
const Coupons = lazy(() => import('./pages/Coupons/Coupons'));
const LoyaltyGrants = lazy(() => import('./pages/LoyaltyGrants/LoyaltyGrants'));
const SystemEvents = lazy(() => import('./pages/SystemEvents/SystemEvents'));
const AdminLogin = lazy(() => import('./pages/AdminLogin/AdminLogin'));

// 보호된 라우트 (RequireAdmin)
import RequireAdmin from './routes/RequireAdmin';
// 토큰 모니터링 서비스
import tokenMonitor from './utils/tokenMonitor';

import './App.css';

function App() {
  // 앱 시작시 토큰 모니터링 초기화
  // httpOnly 쿠키 기반이므로 서버에 인증 상태 확인 후 시작
  useEffect(() => {
    const initMonitoring = async () => {
      const isValid = await tokenMonitor.checkAuthStatus();
      if (isValid) {
        tokenMonitor.startMonitoring();
      }
    };
    initMonitoring();

    // 컴포넌트 언마운트 시 모니터링 정리
    return () => {
      tokenMonitor.stopMonitoring();
    };
  }, []);

  return (
    <ErrorBoundary>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <Routes>
          {/* (1) 로그인 페이지 */}
          <Route path="/admin/login" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminLogin />
            </Suspense>
          } />

          {/* (2) 루트 경로 접속 시 리다이렉션 처리 */}
          <Route path="/" element={<RootRedirect />} />

          {/* (3) 관리자 보호된 라우트 */}
          <Route
            path="/admin/*"
            element={
              <RequireAdmin>
                <Layout />
              </RequireAdmin>
            }
          />

          {/* (4) 다른 모든 경로는 로그인 페이지로 리다이렉트 */}
          <Route path="*" element={<Navigate to="/admin/login" replace />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

/**
 * 루트 경로 처리 컴포넌트
 * - 로그인 상태면 관리자 대시보드로
 * - 비로그인 상태면 로그인 페이지로
 */
function RootRedirect() {
  // httpOnly 쿠키는 JS에서 읽을 수 없으므로, 항상 /admin으로 보내고
  // RequireAdmin에서 서버 인증 확인 후 리다이렉트 처리
  return <Navigate to="/admin" replace />;
}

/**
 * 실제 관리자 페이지 레이아웃 (RequireAdmin 통과 후 렌더)
 *  - 사이드바, 탑바, 내부 <Routes>
 */
function Layout() {
  return (
    <NotificationProvider>
      <div className="app d-flex">
        <Sidebar />
        <div className="main-content flex-grow-1">
          <Topbar />
          <div className="content-wrapper">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* 메인화면: "/" */}
                <Route path="/" element={<Dashboard />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/edit/:id" element={<EditProduct />} />
                <Route path="/products/add" element={<AddProduct />} />
                <Route path="/products/quick-add" element={<QuickAddProduct />} />
                <Route path="/recommends" element={<Recommends />} />
                <Route path="/similarity" element={<Similarity />} />
                <Route path="/themes" element={<Themes />} />
                <Route path="/banners" element={<Banners />} />
                <Route path="/category-icons" element={<CategoryIcons />} />
                <Route path="/home-sections" element={<HomeSections />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/customers/:user_id" element={<CustomerDetail />} />
                <Route path="/product-analytics" element={<ProductAnalytics />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/shopping-list" element={<ShoppingList />} />
                <Route path="/hs-codes" element={<HsCodes />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/orders/:order_id" element={<OrderDetail />} />
                <Route path="/product-requests" element={<ProductRequests />} />
                <Route path="/pickups" element={<Pickups />} />
                <Route path="/tools" element={<Tools />} />
                <Route path="/profit" element={<Profit />} />
                <Route path="/settlements" element={<Settlements />} />
                <Route path="/pricing" element={<PricingSimulator />} />
                <Route path="/price-compare" element={<PriceCompare />} />
                <Route path="/price-setting" element={<PriceSetting />} />
                <Route path="/ops-guide" element={<OpsGuide />} />
                <Route path="/coupons" element={<Coupons />} />
                <Route path="/loyalty-grants" element={<LoyaltyGrants />} />
                <Route path="/system-events" element={<SystemEvents />} />
                {/*
                  추후 "/orders", "/users" 등 다른 라우트도 추가
                */}
              </Routes>
            </Suspense>
          </div>
        </div>
      </div>
      <NotificationToast />
    </NotificationProvider>
  );
}

export default App;
